import { useCallback } from "react";
import {
  BidV2Status,
  SourcingEventStatus,
  type BidV2QualificationDto,
  type BuyerBidV2DetailDto,
  useGetSourcingEventQuery,
  useSetBidQualificationMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { hasTenderStageQualification } from "../../../sourcing-events/buyer/wizard/utils/procedure";
import { getBidQualificationProgress } from "./bidQualification";

export interface BidQualificationDecision {
  applies: boolean;
  decision: BidV2QualificationDto | null;
  decisionOpen: boolean;
  pending: number;
  decide: (qualified: boolean) => Promise<void>;
  isLoading: boolean;
}

export const useBidQualificationDecision = (detail: BuyerBidV2DetailDto): BidQualificationDecision => {
  const { triggerSoft } = useApiError();
  const [setQualification, { isLoading }] = useSetBidQualificationMutation();
  const { data: event } = useGetSourcingEventQuery({ eventId: detail.bid.forEvent });

  const eventId = detail.bid.forEvent;
  const bidId = detail.bid.id;

  const decide = useCallback(
    async (qualified: boolean) => {
      try {
        await setQualification({
          eventId,
          bidId,
          setBidQualificationRequestDto: { qualified },
        }).unwrap();
      } catch (e) {
        triggerSoft(e);
      }
    },
    [setQualification, eventId, bidId, triggerSoft],
  );

  const progress = getBidQualificationProgress(detail);

  return {
    applies:
      detail.bid.status === BidV2Status.Submitted &&
      hasTenderStageQualification(
        event?.eventContext?.type === "Public" ? event.eventContext.procedureType : undefined,
      ),
    decision: detail.qualification ?? null,
    decisionOpen: event?.status === SourcingEventStatus.Published,
    pending: progress.hasRequirements ? progress.pending : 0,
    decide,
    isLoading,
  };
};
